/* eslint-disable no-unused-vars */
var XLSX = require('xlsx');
var { saveAs } = require('file-saver');

/**
 * Derives a flat list of leaf column descriptors from a columnDefs array.
 * Each descriptor: { groupName, headerName, valueGetter, field }
 *   - groupName: parent headerName (empty string for standalone columns)
 *   - headerName: the leaf column label shown in grid
 *   - valueGetter: function(data) -> formatted cell value
 *   - field: fallback field key when no valueGetter
 */
function collectLeafColumns(columnDefs) {
    var leaves = [];
    (columnDefs || []).forEach(function (col) {
        if (Array.isArray(col.children) && col.children.length > 0) {
            col.children.forEach(function (child) {
                leaves.push({
                    groupName: col.headerName || '',
                    headerName: child.headerName || '',
                    valueGetter: typeof child.valueGetter === 'function' ? child.valueGetter : null,
                    field: child.field || null,
                });
            });
        } else {
            leaves.push({
                groupName: '',
                headerName: col.headerName || '',
                valueGetter: typeof col.valueGetter === 'function' ? col.valueGetter : null,
                field: col.field || null,
            });
        }
    });
    return leaves;
}

/**
 * Resolves a cell value for a given row and leaf descriptor.
 * Prefers valueGetter; falls back to plain field access.
 */
function resolveCell(rowData, leaf) {
    if (leaf.valueGetter) {
        try {
            var val = leaf.valueGetter({ data: rowData });
            return (val === null || val === undefined) ? '' : val;
        } catch (e) {
            // fall through to field
        }
    }
    if (leaf.field) {
        var v = rowData[leaf.field];
        return (v === null || v === undefined) ? '' : v;
    }
    return '';
}

/**
 * Collects all data rows from the grid in current sort/filter order.
 * Falls back to rowData array if gridApi is unavailable.
 */
function collectRows(gridApi, rowData) {
    var rows = [];
    if (gridApi && typeof gridApi.forEachNodeAfterFilterAndSort === 'function') {
        try {
            gridApi.forEachNodeAfterFilterAndSort(function (node) {
                if (node.data) rows.push(node.data);
            });
        } catch (e) {
            rows = rowData || [];
        }
    } else {
        rows = rowData || [];
    }
    return rows;
}

/**
 * Exports grid data to XLSX with a proper two-row grouped header:
 *   Row 1: Group names, merged across their child columns (blank for standalone)
 *   Row 2: Leaf column names
 *   Row 3+: Data
 *
 * Uses SheetJS (xlsx) which is already installed in the project.
 */
function exportToExcel(gridApi, columnDefs, rowData, fileName) {
    var safeFileName = (fileName || 'export') + (fileName && fileName.endsWith('.xlsx') ? '' : '.xlsx');
    var leaves = collectLeafColumns(columnDefs);
    if (!leaves.length) return;

    var rows = collectRows(gridApi, rowData);

    // Build a 2D array: [groupHeaderRow, leafHeaderRow, ...dataRows]
    var aoa = []; // array of arrays

    // Row 1 — group headers (repeat group name across its span so merged cell shows correctly)
    var groupRow = leaves.map(function (leaf) { return leaf.groupName; });
    aoa.push(groupRow);

    // Row 2 — leaf headers
    var leafRow = leaves.map(function (leaf) { return leaf.headerName; });
    aoa.push(leafRow);

    // Data rows
    rows.forEach(function (rowObj) {
        var dataRow = leaves.map(function (leaf) { return resolveCell(rowObj, leaf); });
        aoa.push(dataRow);
    });

    var wb = XLSX.utils.book_new();
    var ws = XLSX.utils.aoa_to_sheet(aoa);

    // Apply merged cells for group header row (row index 0, 1-based row 1)
    var merges = [];
    var colIdx = 0;
    (columnDefs || []).forEach(function (col) {
        if (Array.isArray(col.children) && col.children.length > 1) {
            merges.push({
                s: { r: 0, c: colIdx },
                e: { r: 0, c: colIdx + col.children.length - 1 },
            });
            colIdx += col.children.length;
        } else if (Array.isArray(col.children) && col.children.length === 1) {
            colIdx += 1;
        } else {
            colIdx += 1;
        }
    });
    if (merges.length) ws['!merges'] = merges;

    // Style header rows — bold + background colour
    var headerStyle = { font: { bold: true }, fill: { fgColor: { rgb: "1F4E79" }, type: "pattern", patternType: "solid" }, alignment: { horizontal: "center", wrapText: true } };
    var groupStyle  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F4E79" }, type: "pattern", patternType: "solid" }, alignment: { horizontal: "center" } };
    var leafStyle   = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2E75B6" }, type: "pattern", patternType: "solid" }, alignment: { horizontal: "center", wrapText: true } };

    leaves.forEach(function (_, ci) {
        var groupCell = XLSX.utils.encode_cell({ r: 0, c: ci });
        var leafCell  = XLSX.utils.encode_cell({ r: 1, c: ci });
        if (ws[groupCell]) ws[groupCell].s = groupStyle;
        if (ws[leafCell])  ws[leafCell].s  = leafStyle;
    });

    // Auto column widths
    ws['!cols'] = leaves.map(function (leaf) {
        return { wch: Math.max(leaf.headerName.length + 2, leaf.groupName.length + 2, 10) };
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    // Write buffer and trigger download
    var wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), safeFileName);
}

/**
 * Exports grid data to CSV with a two-row grouped header:
 *   Row 1: Group name repeated across its sub-column span (blank for standalone)
 *   Row 2: Leaf column names
 *   Row 3+: Data
 */
function exportToCsv(gridApi, columnDefs, rowData, fileName) {
    var safeFileName = (fileName || 'export') + (fileName && fileName.endsWith('.csv') ? '' : '.csv');
    var leaves = collectLeafColumns(columnDefs);
    if (!leaves.length) return;

    var rows = collectRows(gridApi, rowData);

    function escapeCsv(val) {
        var s = String(val === null || val === undefined ? '' : val);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
    }

    var csvLines = [];

    // Row 1 — group headers
    csvLines.push(leaves.map(function (leaf) { return escapeCsv(leaf.groupName); }).join(','));

    // Row 2 — leaf headers
    csvLines.push(leaves.map(function (leaf) { return escapeCsv(leaf.headerName); }).join(','));

    // Data rows
    rows.forEach(function (rowObj) {
        csvLines.push(leaves.map(function (leaf) { return escapeCsv(resolveCell(rowObj, leaf)); }).join(','));
    });

    var csvContent = csvLines.join('\n');
    var blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, safeFileName);
}

module.exports = { exportToExcel: exportToExcel, exportToCsv: exportToCsv };
